import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { AssetAllocation } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';

import { calculateTotalWeight } from '../../shared-utils';
import { StrategyAssetTable } from '../../strategies/StrategyAssetTable';
import { useAssetTableProps } from '../../strategies/useAssetTableProps';

interface NewStrategyBuilderFormValues {
    assetAllocations: AssetAllocation[];
}

interface NewStrategyBuilderPanelProps {
    modelName: string;
    existingStrategyNames: string[];
    onCommit: (name: string, assetAllocations: AssetAllocation[]) => void;
    onCancel: () => void;
}

export const NewStrategyBuilderPanel: React.FC<NewStrategyBuilderPanelProps> = ({
    modelName,
    existingStrategyNames,
    onCommit,
    onCancel,
}) => {
    const [name, setName] = useState('');
    const form = useForm<NewStrategyBuilderFormValues>({ defaultValues: { assetAllocations: [] } });
    const assetTableProps = useAssetTableProps(form);
    const { assetAllocations } = assetTableProps;

    const trimmedName = name.trim();
    const isNameTaken = existingStrategyNames.some((n) => n.trim().toLowerCase() === trimmedName.toLowerCase());
    const totalWeight = calculateTotalWeight(assetAllocations);
    const isBalanced = totalWeight === 100;
    const isValid = trimmedName.length > 0 && !isNameTaken && assetAllocations.length > 0 && isBalanced;

    const invalidMessage =
        trimmedName.length === 0
            ? 'Name the strategy.'
            : isNameTaken
              ? 'A strategy with this name already exists.'
              : assetAllocations.length === 0
                ? 'Add at least one holding.'
                : !isBalanced
                  ? `Weights total ${totalWeight}% — must be 100%.`
                  : undefined;

    // Weights stay in percent form here — they're converted to decimal fractions once, at the
    // model's final save step (mirrors how existing-strategy weights flow through the form).
    const handleCommit = () => {
        if (!isValid) return;
        onCommit(trimmedName, assetAllocations);
    };

    return (
        <div className="border-cocoa-900 flex flex-col gap-4 rounded-lg border bg-white p-6">
            <div className="flex flex-wrap items-center gap-3">
                <span className="text-cocoa-1000 text-lg font-medium">New strategy</span>
                <span className="text-cocoa-500 text-xs">Saves into “{modelName.trim() || 'this Model'}”</span>
            </div>

            <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                placeholder="Name this strategy"
                className="border-cocoa-200 text-cocoa-1000 placeholder:text-cocoa-300 border-0 border-b bg-transparent pb-0.5 font-sans text-2xl leading-[1.15] font-medium tracking-[-0.02em] outline-none"
            />

            <StrategyAssetTable {...assetTableProps} />

            <div className="flex flex-wrap items-center gap-3">
                {invalidMessage && <span className="text-sm text-red-600">{invalidMessage}</span>}
                <Button type="button" variant="outline" className="ml-auto font-medium" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    type="button"
                    variant="default"
                    className="font-medium"
                    disabled={!isValid}
                    onClick={handleCommit}
                >
                    Add to Model
                </Button>
            </div>
        </div>
    );
};
